/**
 * Fast Results page component for ClarCV application.
 * 
 * Provides quick CV generation options including PDF improvement
 * for fast professional results (PDF and Word improvement).
 * 
 * @component
 * @returns {JSX.Element} Fast results page component
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Fast Results component that offers quick CV generation options.
 * 
 * @returns {JSX.Element} The fast results interface with two main options
 */
const FastResults = () => {
  const navigate = useNavigate();

  /**
   * Handle PDF improvement option.
   */
  const handlePDFImprovement = () => {
    navigate('/dashboard/pdf-improvement');
  };

  /**
   * Handle Word improvement option.
   */
  const handleWordImprovement = () => {
    navigate('/dashboard/word-improvement');
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
            }
            50% {
              box-shadow: 0 0 30px rgba(59, 130, 246, 0.5);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        `}
      </style>
      
      <div className="container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem 0' }}>
        {/* Header */}
        <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <div style={{
            fontSize: '5rem',
            marginBottom: '1.5rem',
            animation: 'float 3s ease-in-out infinite',
            fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
            lineHeight: '1',
            display: 'block'
          }}>
            ⚡
          </div>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: '800',
            color: '#1d4ed8',
            marginBottom: '1rem',
            letterSpacing: '-0.02em'
          }}>
            Resultados rápidos
          </h1>
          <p style={{
            fontSize: '1.3rem',
            color: '#6b7280',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            Genera CVs profesionales en minutos
          </p>
        </div>

        {/* Main Options */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {/* PDF Improvement Option */}
          <div 
            onClick={handlePDFImprovement}
            style={{
              backgroundColor: '#fff',
              borderRadius: '20px',
              padding: '2.5rem',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              border: '2px solid #e5e7eb',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
              animation: 'fadeInUp 0.6s ease-out'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-5px)';
              e.target.style.boxShadow = '0 20px 60px rgba(0,0,0,0.15)';
              e.target.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 10px 40px rgba(0,0,0,0.1)';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
              fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
              lineHeight: '1',
              display: 'block'
            }}>
              📄
            </div>
            
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '1rem',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              lineHeight: '1.2'
            }}>
              Mejorar PDF
            </h3>
            
            <p style={{
              color: '#6b7280',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '1.5rem'
            }}>
              Sube tu CV y obtén mejoras al instante con IA: formato, contenido y compatibilidad ATS.
            </p>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}>
              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>•</span>
              <span>Análisis y mejoras al instante</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontSize: '0.9rem',
              fontWeight: '500',
              marginTop: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>•</span>
              <span>Formato profesional</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontSize: '0.9rem',
              fontWeight: '500',
              marginTop: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>•</span>
              <span>Salida optimizada para ATS</span>
            </div>
          </div>

          {/* Word Improvement Option */}
          <div 
            onClick={handleWordImprovement}
            style={{
              backgroundColor: '#fff',
              borderRadius: '20px',
              padding: '2.5rem',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              border: '2px solid #e5e7eb',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
              animation: 'fadeInUp 0.7s ease-out'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-5px)';
              e.target.style.boxShadow = '0 20px 60px rgba(0,0,0,0.15)';
              e.target.style.borderColor = '#3b82f6';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 10px 40px rgba(0,0,0,0.1)';
              e.target.style.borderColor = '#e5e7eb';
            }}
          >
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
              fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
              lineHeight: '1',
              display: 'block'
            }}>
              📝
            </div>
            
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '700',
              color: '#1f2937',
              marginBottom: '1rem',
              fontFamily: 'system-ui, -apple-system, sans-serif',
              lineHeight: '1.2'
            }}>
              Mejorar Word
            </h3>
            
            <p style={{
              color: '#6b7280',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '1.5rem'
            }}>
              Sube tu CV en Word, mantén el diseño y mejora el contenido con IA.
            </p>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}>
              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>•</span>
              <span>Mantiene el diseño original</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontSize: '0.9rem',
              fontWeight: '500',
              marginTop: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>•</span>
              <span>Mejora del contenido con IA</span>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#059669',
              fontSize: '0.9rem',
              fontWeight: '500',
              marginTop: '0.5rem'
            }}>
              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>•</span>
              <span>Descarga en Word editable</span>
            </div>
          </div>
        </div>

        {/* Features Comparison */}
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid #e2e8f0',
          marginBottom: '2rem'
        }}>
          <h3 style={{
            textAlign: 'center',
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1f2937',
            marginBottom: '1.5rem'
          }}>
            ¿Por qué elegir resultados rápidos?
          </h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚡</div>
              <h4 style={{ color: '#1f2937', fontWeight: '600', marginBottom: '0.5rem' }}>Muy rápido</h4>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Resultados en minutos</p>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🤖</div>
              <h4 style={{ color: '#1f2937', fontWeight: '600', marginBottom: '0.5rem' }}>Con IA</h4>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>IA avanzada para mejores resultados</p>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎯</div>
              <h4 style={{ color: '#1f2937', fontWeight: '600', marginBottom: '0.5rem' }}>A tu medida</h4>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Adaptado a tu sector y puesto</p>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✨</div>
              <h4 style={{ color: '#1f2937', fontWeight: '600', marginBottom: '0.5rem' }}>Profesional</h4>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Formatos y contenido aprobados por RR. HH.</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div style={{ textAlign: 'center' }}>
          <button 
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: '#6b7280',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#4b5563';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6b7280';
            }}
          >
            ← Volver al panel
          </button>
        </div>
      </div>
    </>
  );
};

export default FastResults;
